'use client';

import React, { useState } from 'react';
import { SpecialGemCard } from '@/components/cards/SpecialGemCard';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { SPECIAL_GEMS, type SpecialGem as SpecialGemType } from '@repo/utils';
import { IconVerified, IconTopRated, IconRentStabilized } from '@repo/svgs';
import Link from 'next/link';
import { RentStabilizedDownloadModal } from '@/components/modal/RentStabilizedDownloadModal';

// Icon mapping for special gems
const iconMap = {
  IconVerified,
  IconTopRated,
  IconRentStabilized,
} as const;

// Gems that intercept the click to open a download-prompt modal
// instead of navigating to the filtered listings page. The
// rent-stabilized experience is much better in the app (full map,
// calculator, etc.) than on web, so we send users to install rather
// than to a stripped-down web view.
const MODAL_GEM_ICON = 'IconRentStabilized';

export function SpecialGems() {
  const [rentStabModalOpen, setRentStabModalOpen] = useState(false);

  return (
    <div className="dynamic-grid-container">
      <section className="grid md:grid-cols-12 grid-cols-1 gap-4 md:px-4 px-2">
        {/* Section Header - spans full width */}
        <div className="col-span-full -mx-2 -my-2 md:-mx-4">
          <SectionHeader title="Special gems" />
        </div>
        {SPECIAL_GEMS.map((gem: SpecialGemType) => {
          const IconComponent = iconMap[gem.iconName];
          const isModalGem = gem.iconName === MODAL_GEM_ICON;

          const cardBody = (
            <SpecialGemCard
              icon={<IconComponent />}
              title={gem.title}
              description={gem.description}
            />
          );

          // Rent-stabilized card: intercept the click and open the
          // download-prompt modal instead of navigating. Rendered as a
          // button so keyboard + a11y still work correctly.
          if (isModalGem) {
            return (
              <button
                key={gem.title}
                type="button"
                onClick={() => setRentStabModalOpen(true)}
                className="col-span-4 md:col-span-4 lg:col-span-4 text-left"
                data-analytics-event={gem.analyticsEvent}
                data-analytics-props={JSON.stringify({})}
              >
                {cardBody}
              </button>
            );
          }

          // Everything else: unchanged — navigate to the filter URL.
          return (
            <Link
              href={gem.href}
              key={gem.title}
              className="col-span-4 md:col-span-4 lg:col-span-4"
              data-analytics-event={gem.analyticsEvent}
              data-analytics-props={JSON.stringify({})}
            >
              {cardBody}
            </Link>
          );
        })}
      </section>

      <RentStabilizedDownloadModal
        open={rentStabModalOpen}
        onClose={() => setRentStabModalOpen(false)}
        source="home_special_gems"
      />
    </div>
  );
}
